<script>
	import SvelteSeo from 'svelte-seo';
	import Button from '$lib/components/button.svelte';
	import Footer from '$lib/components/footer.svelte';
	import Header from '$lib/components/header.svelte';
</script>

<SvelteSeo
	title="Erbrecht | Kostenlose Antworten auf Deine Fragen"
	description="Auf frag-den-lenssen.de bieten wir Dir eine einfache und verständliche Möglichkeit, Antworten auf Deine Fragen zum Thema Erben zu erhalten. Unser digitaler Zwilling, basierend auf dem Fachwissen von Ingo Lenßen, steht Dir rund um die Uhr zur Verfügung, um Klarheit über Deine Rechte und Pflichten als Erbe zu schaffen."
	openGraph={{
		title: 'Erbrecht | Kostenlose Antworten auf Deine Fragen',
		description:
			'Auf frag-den-lenssen.de bieten wir Dir eine einfache und verständliche Möglichkeit, Antworten auf Deine Fragen zum Thema Erben zu erhalten. Unser digitaler Zwilling, basierend auf dem Fachwissen von Ingo Lenßen, steht Dir rund um die Uhr zur Verfügung, um Klarheit über Deine Rechte und Pflichten als Erbe zu schaffen.'
	}}
	canonical="https://frag-den-lenssen.de/rechtsgebiete/erbrecht"
/>

<main>
	<Header />
	<section class="relative mt-24 bg-gray-50">
		<div class="container max-w-[85ch] flex flex-col justify-center py-20">
			<div class="relative z-10">
				<h1
					class="mb-8 max-w-3xl text-balance text-3xl font-bold text-gray-800 sm:text-4xl md:text-5xl"
				>
					Erben und Nachlassregelungen bei <span class="text-primary">Frag den Lenßen</span>
				</h1>

				<div class="prose text-lg">
					<p>
						Das Erben und die Regelung des Nachlasses sind zentrale Aspekte des Erbrechts und oft
						mit komplexen rechtlichen Fragestellungen verbunden. Auf <b>Frag den Lenßen</b> bieten
						wir Dir eine einfache und verständliche Möglichkeit, Antworten auf Deine Fragen zum
						Thema Erben zu erhalten. Unser digitaler Zwilling, basierend auf dem Fachwissen von
						<b>Ingo Lenßen</b>, steht Dir rund um die Uhr zur Verfügung, um Klarheit über Deine
						Rechte und Pflichten als Erbe zu verschaffen.
					</p>
				</div>

				<div class="mt-8">
					<Button href="#more">Mehr erfahren</Button>
				</div>
			</div>
		</div>
	</section>
	<section class="py-12" id="more">
		<div class="container max-w-[85ch] py-8">
			<h2 class="mb-3.5 text-2xl font-bold text-gray-800 sm:text-3xl md:text-4xl">
				Erben – Wer erhält was?
			</h2>
			<div class="prose mt-6 text-lg">
				<p>
					Das Erben umfasst die Übertragung von Vermögenswerten und Pflichten von einer verstorbenen
					Person (dem Erblasser) auf die Erben. Hierbei gibt es mehrere wichtige Aspekte zu
					beachten:
				</p>
				<h3>1. Erbfolge nach gesetzlichem Erbrecht</h3>
				<p>
					Wenn der Erblasser kein Testament hinterlassen hat, regelt das gesetzliche Erbrecht die
					Erbfolge. Die gesetzliche Erbfolge bestimmt, wer in welcher Reihenfolge erbt:
				</p>
				<ul>
					<li>
						<b>Verwandte:</b> Die Erben erster Ordnung sind die Kinder des Erblassers. Falls diese verstorben
						sind, treten deren Nachkommen an ihre Stelle.
					</li>
					<li>
						<b>Ehepartner:</b> Der Ehepartner des Erblassers erbt ebenfalls nach den gesetzlichen Regelungen.
						Die Höhe des Erbteils kann je nach Güterstand der Ehe variieren (Zugewinngemeinschaft, Gütertrennung
						etc.).
					</li>
					<li>
						<b>Verwandte zweiter Ordnung:</b> Sollten keine Erben erster Ordnung vorhanden sein, erben
						die Eltern des Erblassers und deren Nachkommen.
					</li>
				</ul>
				<p>
					Falls keine Verwandten vorhanden sind, erbt der Staat gemäß der gesetzlichen Erbfolge.
				</p>
				<h3>2. Testament und Erbvertrag</h3>
				<p>
					Um von der gesetzlichen Erbfolge abzuweichen, kann der Erblasser ein Testament oder einen
					Erbvertrag erstellen. Diese Dokumente regeln, wie der Nachlass verteilt werden soll:
				</p>
				<ul>
					<li>
						<b>Testament:</b> Ein Testament kann frei gestaltet werden, solange es den gesetzlichen Formvorschriften
						entspricht. Der Erblasser kann bestimmen, wer erben soll, und auch spezielle Verfügungen
						treffen (z.B. Erbeinsetzung, Vermächtnisse).
					</li>
					<li>
						<b>Erbvertrag:</b> Ein Erbvertrag ist eine notarielle Vereinbarung zwischen dem Erblasser
						und einem oder mehreren Erben. Der Vertrag ist bindend und kann nicht einseitig geändert
						werden.
					</li>
				</ul>
				<h3>3. Pflichtteil</h3>
				<p>
					Der Pflichtteil ist ein gesetzlicher Anspruch, der bestimmten Personen, wie den Kindern
					und dem Ehepartner, zusteht. Auch wenn sie im Testament nicht bedacht werden, haben diese
					Personen Anspruch auf einen Pflichtteil des Nachlasses. Der Pflichtteil beträgt die Hälfte
					des gesetzlichen Erbteils.
				</p>
			</div>
		</div>

		<div class="container max-w-[85ch] py-8">
			<h2 class="mb-3.5 text-2xl font-bold text-gray-800 sm:text-3xl md:text-4xl">
				Aufgaben und Pflichten der Erben
			</h2>
			<div class="prose mt-6 text-lg">
				<h3>1. Erbschaft annehmen oder ausschlagen</h3>
				<p>
					Nach dem Tod des Erblassers hast Du die Möglichkeit, die Erbschaft anzunehmen oder
					auszuschlagen. Die Ausschlagung der Erbschaft ist möglich, wenn Du das Erbe nicht antreten
					möchtest, zum Beispiel, weil es mit Schulden belastet ist. Beachte jedoch, dass eine
					Ausschlagung innerhalb von sechs Wochen nach Kenntnis des Erbfalls erklärt werden muss.
				</p>
				<h3>2. Nachlassregelung</h3>
				<p>
					Der Nachlass umfasst alle Vermögenswerte und Verbindlichkeiten des Erblassers. Du bist als
					Erbe verpflichtet, den Nachlass zu regeln, was bedeutet:
				</p>
				<ul>
					<li>
						<b>Vermögenswerte ermitteln:</b> Finde alle Vermögenswerte des Erblassers (z.B. Immobilien,
						Bankkonten, Wertgegenstände) und erstelle eine Nachlassübersicht.
					</li>
					<li>
						<b>Verbindlichkeiten begleichen:</b> Bezahle alle bestehenden Schulden und Verbindlichkeiten
						aus dem Nachlass, bevor die verbleibenden Mittel verteilt werden.
					</li>
					<li>
						<b>Erbschaftsteuer:</b> Die Erbschaftsteuer muss gegebenenfalls bezahlt werden. Die Höhe
						der Steuer hängt vom Wert des Erbes und der Beziehung zwischen Erblasser und Erbe ab.
					</li>
				</ul>
			</div>
		</div>

		<div class="container max-w-[85ch] py-8">
			<h2 class="mb-3.5 text-2xl font-bold text-gray-800 sm:text-3xl md:text-4xl">
				Häufige Fragen zum Erben
			</h2>
			<div class="prose mt-6 text-lg">
				<ul>
					<li>
						<b>Wie wird ein Testament rechtlich wirksam?</b> Ein Testament muss bestimmten Formvorschriften
						entsprechen (eigenhändig geschrieben und unterschrieben oder notariell beurkundet). Bei Nichteinhaltung
						der Vorschriften ist das Testament möglicherweise unwirksam.
					</li>
					<li>
						<b>Wie kann ich meinen Pflichtteil geltend machen?</b> Wenn Du als Pflichtteilsberechtigter
						einen Anspruch geltend machen möchtest, solltest Du diesen schriftlich gegenüber den anderen
						Erben anmelden. Bei Problemen kann rechtliche Beratung erforderlich sein.
					</li>
					<li>
						<b>Was passiert, wenn die Erben uneinig sind?</b> Bei Uneinigkeit unter den Erben kann es
						zu Konflikten kommen. In solchen Fällen kann eine Mediation oder rechtliche Unterstützung
						sinnvoll sein, um eine einvernehmliche Lösung zu finden.
					</li>
				</ul>
			</div>
		</div>

		<div class="container max-w-[85ch] py-8">
			<h2 class="mb-3.5 text-2xl font-bold text-gray-800 sm:text-3xl md:text-4xl">Fazit</h2>
			<div class="prose mt-6 text-lg">
				<p>
					Das Erbrecht ist vielschichtig und kann komplex sein. Mit <b>Frag den Lenßen</b> erhältst
					Du eine moderne und unkomplizierte Lösung für Deine Fragen rund um das Thema Erben. Egal,
					ob es um die gesetzliche Erbfolge, Testamente, Pflichtteile oder die Nachlassregelung geht
					– unser digitaler Zwilling von <b>Ingo Lenßen</b> bietet Dir schnelle und verständliche Antworten.
				</p>
			</div>
		</div>
	</section>
	<section class="bg-primary text-primary-foreground">
		<div class="container max-w-[85ch] py-20">
			<h2 class="text-balance text-2xl font-bold sm:text-3xl md:text-4xl">
				Teste den Frag den Lenßen-Chat noch heute und erhalte die rechtliche Klarheit, die Du
				benötigst!
			</h2>
			<p class="mt-4 text-lg">Jetzt Ingo Lenßen fragen</p>
			<div class="mt-10 flex items-center gap-x-6">
				<Button rel="external" href="/chat" variant="white">Hier geht's zum Erbrecht-Chat</Button>
			</div>
		</div>
	</section>
</main>
<Footer />
